import { useMemo, useRef } from 'react';
import { faClose, faGridRound2 } from '@fortawesome/pro-solid-svg-icons';
import { useOutsideClick } from 'framework/dropdown/hooks/useOutsideClick';
import { Icon } from 'framework/Icon';

import { EMPTY_FUNCTION, POPOVER_ID } from '../../constants';

import { PopoverContext, usePopoverContext } from './context/PopoverContext';
import { usePopoverDrag } from './hooks/usePopoverDrag';
import { usePopoverEscape } from './hooks/usePopoverEscape';
import { usePopoverTheme } from './hooks/usePopoverTheme';
import { PopoverCloseElement, PopoverElement, PopoverGripElement } from './elements';
import { PopoverBodyProps, PopoverFooterProps, PopoverHeaderProps, PopoverProps } from './types';

import cn from './Popover.module.scss';

export const Popover = ({
  is_open = false,
  identifier,
  right,
  duration = 300,
  width = '200px',
  gap = '16px',
  height = 'max-content',
  onSubmit = EMPTY_FUNCTION,
  onClose = EMPTY_FUNCTION,
  dismiss = false,
  theme,
  children,
}: PopoverProps) => {
  const popoverRef = useRef(null);
  const gripRef = useRef(null);

  usePopoverDrag(popoverRef, gripRef, is_open);
  usePopoverEscape(identifier, () => onClose({ [identifier]: is_open }));

  useOutsideClick({
    refs: [popoverRef],
    enabled: is_open,
    callback: () => {
      if (!dismiss) return;
      onClose({ [identifier]: is_open });
    },
  });

  const popover_theme = usePopoverTheme(theme);

  const context = useMemo(
    () => ({ theme, gripRef, is_open, identifier, onClose }),
    [theme, is_open, identifier, onClose]
  );

  if (!is_open) {
    return null;
  }

  return (
    <PopoverContext.Provider value={context}>
      <PopoverElement
        id={POPOVER_ID}
        ref={popoverRef}
        className={cn.popover}
        data-identifier={identifier}
        theme={popover_theme}
        gap={gap}
        width={width}
        height={height}
        right={right}
        is_visible={is_open}
        duration={duration}
        onSubmit={onSubmit}
      >
        {children}
      </PopoverElement>
    </PopoverContext.Provider>
  );
};

const Header = ({ dismiss = true }: PopoverHeaderProps) => {
  const { gripRef, onClose, identifier, theme, is_open } = usePopoverContext();
  const popover_theme = usePopoverTheme(theme);
  return (
    <div className={cn.header}>
      <PopoverGripElement className={cn.grip} ref={gripRef} theme={popover_theme}>
        <Icon icon={faGridRound2} size="11px" width="42px" />
      </PopoverGripElement>
      {dismiss && (
        <PopoverCloseElement
          className={cn.close}
          onClick={() => onClose({ [identifier]: is_open })}
          theme={popover_theme}
        >
          <Icon icon={faClose} size="14px" />
        </PopoverCloseElement>
      )}
    </div>
  );
};

const Body = ({ children, gap = '8px', padding = '0px 16px 16px 16px' }: PopoverBodyProps) => {
  return (
    <div className={cn.body} style={{ gap, padding }}>
      {children}
    </div>
  );
};

Popover.Footer = ({ children, margin = '16px' }: PopoverFooterProps) => {
  return (
    <div className={cn.footer} style={{ margin }}>
      {children}
    </div>
  );
};

Popover.Header = Header;
Popover.Body = Body;
